import { MAX_UINT256 } from '@emberswap/emberswap-sdk2/dist/constants'
import { Amount } from '@emberswap/emberswap-sdk2/dist/entities'
import { Type } from '@emberswap/emberswap-sdk2/dist/types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { encodeFunctionData, TransactionRequest, UserRejectedRequestError, zeroAddress } from 'viem'
import {
  Address,
  erc20ABI,
  useAccount,
  usePrepareSendTransaction,
  useSendTransaction,
  useWalletClient,
} from 'wagmi'
import { getContract, SendTransactionResult, waitForTransaction } from 'wagmi/actions'

import { createErrorToast } from '../components/Toast'
import { calculateGasMargin } from '../functions/trade/calculateGasMargin'
import { useNotifications } from '../state/user/hooks'
import { useERC20Allowance } from './useERC20Allowance'

export enum ApprovalState {
  LOADING = 'LOADING',
  UNKNOWN = 'UNKNOWN',
  NOT_APPROVED = 'NOT_APPROVED',
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
}

/** returns a variable indicating the state of the approval and a function which approves if necessary or early returns
 * @param {boolean} watch
 * @param {Amount<Type>} amountToApprove
 * @param {string} spender
 * @param {boolean} approveMax
 * @param {NotificationData} onSuccess notification handler
 * @return {[ApprovalState, void]}
 */
export function useERC20ApproveCallback(
  watch: boolean,
  amountToApprove?: Amount<Type>,
  spender?: string,
  approveMax?: boolean,
): [ApprovalState, () => void] {
  const { address } = useAccount()
  const walletClient = useWalletClient()
  const [, {createNotification}] = useNotifications(address)
  // console.log(amountToApprove)
  const onSettled = useCallback(
    (data: SendTransactionResult | undefined, e: Error | null) => {
      // TODO: ignore until wagmi workaround on ethers error
      // @ts-ignore
      if (e) {
        createErrorToast(e?.message, true)
      }
      if (data && amountToApprove) {
        const ts = new Date().getTime()
        createNotification({
          type: 'approval',
          chainId: amountToApprove.currency.chainId,
          txHash: data.hash,
          promise: waitForTransaction({
            hash: data.hash,
            chainId: amountToApprove.currency.chainId,
          }),
          summary: {
            pending: `Approving ${amountToApprove.currency.symbol}`,
            completed: `Successfully approved ${amountToApprove.currency.symbol}`,
            failed: `Something went wrong approving ${amountToApprove.currency.symbol}`,
          },
          groupTimestamp: ts,
          timestamp: ts,
        })
      }
    },
    [amountToApprove, createNotification]
  )

  const [request, setRequest] = useState<TransactionRequest>()
  const { config } = usePrepareSendTransaction({
    chainId: amountToApprove?.currency.chainId,
    data: request?.data,
    account: request?.from,
    to: request?.to,
    value: request?.value,
  })

  const { sendTransaction, isLoading: isWritePending } = useSendTransaction({
    ...config,
    onSettled,
  })

  const token = amountToApprove?.currency?.isToken ? amountToApprove.currency : undefined
  const { data: currentAllowance, isLoading } = useERC20Allowance(
    watch,
    token,
    address ?? undefined,
    spender
  )

  // check the current approval status
  const approvalState: ApprovalState = useMemo(() => {
    if (isLoading) return ApprovalState.LOADING
    if (!amountToApprove || !spender) return ApprovalState.UNKNOWN
    if (amountToApprove.currency.isNative) return ApprovalState.APPROVED
    if (isWritePending) return ApprovalState.PENDING

    // We might not have enough data to know whether we need to approve
    if (!currentAllowance) return ApprovalState.UNKNOWN

    // amountToApprove will be defined if currentAllowance is
    return currentAllowance.lessThan(amountToApprove)
      ? ApprovalState.NOT_APPROVED
      : ApprovalState.APPROVED
  }, [amountToApprove, currentAllowance, isLoading, isWritePending, spender])

  const tokenContract = getContract({
    address: (token?.address as Address) ?? zeroAddress,
    abi: erc20ABI,
    walletClient,
  })

  const prepare = useCallback(async (): Promise<void> => {
    try {
      if (
        !amountToApprove?.currency.chainId ||
        approvalState !== ApprovalState.NOT_APPROVED ||
        !token ||
        !tokenContract ||
        !amountToApprove ||
        !spender
      ) {
        return
      }

      const estimatedGas = await tokenContract.estimateGas
        .approve([spender as Address, BigInt(amountToApprove.quotient.toString())], {
          account: spender as Address,
        })
        .catch(() => {
          // General fallback for tokens who restrict approval amounts
          return tokenContract.estimateGas.approve(
            [spender as Address, BigInt(amountToApprove.quotient.toString())],
            {
              gasPrice: undefined,
              maxFeePerGas: undefined,
              maxPriorityFeePerGas: undefined,
              gas: undefined,
              nonce: undefined,
              value: undefined,
              accessList: undefined,
              type: undefined,
              account: spender as Address,
              blockNumber: undefined,
              blockTag: undefined,
            }
          )
        })

      setRequest({
        from: address!,
        to: tokenContract?.address,
        data: encodeFunctionData({
          abi: tokenContract.abi,
          functionName: 'approve',
          args: [
            spender as Address,
            !approveMax
              ? BigInt(amountToApprove.quotient.toString())
              : BigInt(MAX_UINT256.toString()),
          ],
        }),
        gas: calculateGasMargin(estimatedGas),
      })
    } catch (e: unknown) {
      if (e instanceof UserRejectedRequestError) return
      console.error(e)
    }
  }, [amountToApprove, approvalState, token, tokenContract, spender, address, approveMax])

  // Prepare transaction
  useEffect(() => {
    void prepare()
  }, [prepare])

  return useMemo(() => [approvalState, () => sendTransaction?.()], [approvalState, sendTransaction])
}
